import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchBillingRates,
  createBillingRate,
  updateBillingRate,
  deleteBillingRate,
} from '../../store/actions/FinanceActions';
import Table from '../shared/TailwindComponents/Table';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import {
  PiPlusLight,
  PiPencilSimpleLight,
  PiTrashLight,
  PiScalesLight,
  PiXLight,
} from 'react-icons/pi';
import { FinanceBillingRate } from '../../store/reducer/FinanceReducer';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const rateTypeBadgeColor: Record<string, any> = {
  hourly: 'blue',
  fixed: 'green',
  contingency: 'purple',
  retainer: 'yellow',
  flat: 'gray',
};

const RATE_TYPE_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'fixed', label: 'Fixed Fee' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'flat', label: 'Flat Rate' },
  { value: 'blended', label: 'Blended Rate' },
  { value: 'pro_bono', label: 'Pro Bono' },
  { value: 'custom', label: 'Custom...' },
];

const SummaryCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-xl p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-medium mt-1 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
      {value}
    </p>
  </div>
);

const BillingRatesTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const billingRates = useAppSelector((state: RootState) => state.finance.billingRates);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const preferredCurrency = useAppSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings?.currency) || 'usd';

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRate, setEditingRate] = useState<FinanceBillingRate | null>(null);

  // Compute billing rates summary
  const ratesSummary = React.useMemo(() => {
    const totalRates = billingRates.length;
    const defaultRates = billingRates.filter((r: FinanceBillingRate) => r.is_default).length;
    const nonContingency = billingRates.filter((r: FinanceBillingRate) => r.rate_type !== 'contingency');
    const avgRate = nonContingency.length
      ? Math.round(nonContingency.reduce((sum: number, r: FinanceBillingRate) => sum + r.amount, 0) / nonContingency.length)
      : 0;
    const currency = billingRates[0]?.currency || preferredCurrency;
    return { totalRates, defaultRates, avgRate, currency };
  }, [billingRates]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchBillingRates(organizationId));
    }
  }, [dispatch, organizationId]);

  // Listen for header "New Rate" button
  useEffect(() => {
    const handler = () => setShowCreateForm(true);
    window.addEventListener('finance:new-rate', handler);
    return () => window.removeEventListener('finance:new-rate', handler);
  }, []);

  const refreshList = () => {
    if (!organizationId) return;
    dispatch(fetchBillingRates(organizationId));
  };

  const handleDelete = async (id: number) => {
    if (!organizationId) return;
    await dispatch(deleteBillingRate(id, organizationId));
    refreshList();
  };

  const getScopeLabel = (rate: FinanceBillingRate): string => {
    const parts: string[] = [];
    if (rate.matter_id) parts.push(t('Matter'));
    if (rate.client_id) parts.push(t('Client'));
    if (rate.user_id) parts.push(t('User'));
    if (parts.length === 0) return t('Organization Default');
    return parts.join(' + ');
  };

  const columns = ['Type', 'Amount', 'Scope', 'Default', 'Description', 'Effective'];

  const tableData = billingRates.map((rate: FinanceBillingRate) => ({
    id: rate.id,
    Type: rate.rate_type,
    Amount: rate.amount,
    Scope: getScopeLabel(rate),
    Default: rate.is_default,
    Description: rate.description || '—',
    Effective: rate.effective_from
      ? `${new Date(rate.effective_from).toLocaleDateString()}${rate.effective_to ? ` – ${new Date(rate.effective_to).toLocaleDateString()}` : ''}`
      : t('Always'),
    _currency: rate.currency,
    _raw: rate,
  }));

  const renderCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Type':
        return (
          <Badge color={rateTypeBadgeColor[value] || 'gray'}>
            {(value || '').replace(/_/g, ' ')}
          </Badge>
        );
      case 'Amount':
        return (
          <span className="font-medium text-gray-900 dark:text-white">
            {row._raw.rate_type === 'contingency'
              ? `${(value / 100).toFixed(1)}%`
              : formatCurrency(value, row._currency)}
          </span>
        );
      case 'Scope':
        return (
          <span className="text-gray-600 dark:text-gray-400 text-xs">
            {value}
          </span>
        );
      case 'Default':
        return value ? (
          <Badge color="green">{t('Default')}</Badge>
        ) : null;
      case 'Description':
        return (
          <span className="text-gray-500 dark:text-gray-400 max-w-xs truncate block">
            {value}
          </span>
        );
      default:
        return <span className="text-gray-600 dark:text-gray-400">{value}</span>;
    }
  };

  const tableActions = [
    {
      label: t('Edit'),
      icon: <PiPencilSimpleLight className="w-4 h-4" />,
      onClick: (row: any) => setEditingRate(row._raw),
    },
    {
      label: t('Delete'),
      icon: <PiTrashLight className="w-4 h-4" />,
      onClick: (row: any) => handleDelete(row.id),
      isDestructive: true,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 px-4 pt-4">
        <SummaryCard
          label={t('Total Rates')}
          value={String(ratesSummary.totalRates)}
        />
        <SummaryCard
          label={t('Default Rates')}
          value={String(ratesSummary.defaultRates)}
          highlight
        />
        <SummaryCard
          label={t('Avg. Rate')}
          value={formatCurrency(ratesSummary.avgRate, ratesSummary.currency)}
        />
      </div>

      {/* Create / Edit Modal */}
      <BillingRateModal
        isOpen={showCreateForm || !!editingRate}
        organizationId={organizationId!}
        rate={editingRate}
        onClose={() => { setShowCreateForm(false); setEditingRate(null); }}
        onSaved={() => {
          setShowCreateForm(false);
          setEditingRate(null);
          refreshList();
        }}
      />

      {/* Table */}
      <div className="px-4 pt-2 flex-1 flex flex-col min-h-0">
        <Table
          columns={columns}
          data={tableData}
          renderCell={renderCell}
          actions={tableActions}
          emptyState={{
            icon: <PiScalesLight className="w-10 h-10" />,
            headline: t('No billing rates configured'),
          }}
        />
      </div>
    </div>
  );
};

// ── Billing Rate Modal (Create / Edit) ─────────────────────────

const BillingRateModal: React.FC<{
  isOpen: boolean;
  organizationId: number;
  rate: FinanceBillingRate | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ isOpen, organizationId, rate, onClose, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);

  const isKnownType = (t: string) =>
    RATE_TYPE_OPTIONS.some(o => o.value === t && o.value !== 'custom');

  const [rateType, setRateType] = useState(rate?.rate_type ? (isKnownType(rate.rate_type) ? rate.rate_type : 'custom') : 'hourly');
  const [customRateType, setCustomRateType] = useState(
    rate?.rate_type && !isKnownType(rate.rate_type) ? rate.rate_type : ''
  );
  const [amount, setAmount] = useState(rate ? (rate.amount / 100).toFixed(2) : '');
  const [description, setDescription] = useState(rate?.description || '');
  const [isDefault, setIsDefault] = useState(rate?.is_default ?? false);
  const [effectiveFrom, setEffectiveFrom] = useState(
    rate?.effective_from ? rate.effective_from.split('T')[0] : ''
  );
  const [effectiveTo, setEffectiveTo] = useState(
    rate?.effective_to ? rate.effective_to.split('T')[0] : ''
  );

  // Reset form when rate changes (edit vs create)
  useEffect(() => {
    if (isOpen) {
      const rt = rate?.rate_type || 'hourly';
      const known = isKnownType(rt);
      setRateType(known ? rt : 'custom');
      setCustomRateType(known ? '' : rt);
      setAmount(rate ? (rate.amount / 100).toFixed(2) : '');
      setDescription(rate?.description || '');
      setIsDefault(rate?.is_default ?? false);
      setEffectiveFrom(rate?.effective_from ? rate.effective_from.split('T')[0] : '');
      setEffectiveTo(rate?.effective_to ? rate.effective_to.split('T')[0] : '');
    }
  }, [isOpen, rate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!amountCents || amountCents <= 0) return;

    const resolvedType = rateType === 'custom' ? (customRateType.trim() || 'custom') : rateType;

    const payload = {
      organization_id: organizationId,
      rate_type: resolvedType,
      amount: amountCents,
      description: description || undefined,
      is_default: isDefault,
      effective_from: effectiveFrom || undefined,
      effective_to: effectiveTo || undefined,
    };

    let result;
    if (rate) {
      result = await dispatch(updateBillingRate(rate.id, payload));
    } else {
      result = await dispatch(createBillingRate(payload));
    }

    if (result) {
      onSaved();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {rate ? t('Edit Billing Rate') : t('New Billing Rate')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <PiXLight className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Dropdown
                options={RATE_TYPE_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }))}
                value={rateType}
                onChange={(val) => setRateType(val as string)}
                label={t('Rate Type')}
              />
              <Input
                label={rateType === 'contingency' ? t('Percentage (%)') : t('Amount ($)')}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={rateType === 'contingency' ? '33.33' : '0.00'}
              />
            </div>

            {rateType === 'custom' && (
              <Input
                label={t('Custom Rate Type Name')}
                type="text"
                value={customRateType}
                onChange={(e) => setCustomRateType(e.target.value)}
                placeholder={t('e.g., Success Fee, Capped Fee...')}
              />
            )}

            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsDefault(!isDefault)}
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                  isDefault ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}>
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                    isDefault ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('Organization Default')}</span>
              </button>
            </div>

            <Textarea
              id="rate-description"
              label={t('Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('Rate description...')}
              rows={2}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('Effective From')}
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
              <Input
                label={t('Effective To')}
                type="date"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" size="small" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              size="small"
              type="submit"
              isLoading={loading?.createBillingRate || loading?.updateBillingRate}
            >
              {rate ? t('Save Changes') : t('Add Rate')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingRatesTab;
